import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { Pair as PairData, usePairs } from '@/store/pairs';
import { usePrice } from '@/store/price';
import { formatDisplay } from '@/utils/format-display';
import { throttle } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react'; // Imported React for React.memo
import { Link } from 'react-router'; // Assuming this is react-router v3 or similar. For v6 it would be react-router-dom
import { useShallow } from 'zustand/shallow';
import ChartView from './chart';


// --- Type Definitions (Inferred from usage) ---
export interface Pair {
    pairIndex: number;
    symbol: string;
    imageUrl: string;
    decimalsToShow: number;
    group: number; // Used for crypto filter (e.g., 0 for crypto)
}

interface PriceInfo {
    price?: string | number;
    price24h?: string | number;
}

interface PriceStoreData {
    [pairIndex: string]: PriceInfo | undefined;
}

// Assuming state structures for Zustand stores
export interface PairsState {
    data: PairData[];
}

export interface PriceState {
    data: PriceStoreData;
}

interface Price24hProps {
    rawPrice24h: number | string | undefined;
}

const Price24h = React.memo(function Price24h({ rawPrice24h }: Price24hProps) {
    const [throttledPrice24h, setThrottledPrice24h] = useState<number | null>(
        null
    );
    const throttledUpdate = useRef(
        throttle((newPrice: number) => {
            setThrottledPrice24h(newPrice);
        }, 1000)
    ).current;

    useEffect(() => {
        if (rawPrice24h != null) {
            throttledUpdate(Number(rawPrice24h));
        }
    }, [rawPrice24h, throttledUpdate]);

    const prefix =
        throttledPrice24h != null && throttledPrice24h >= 0 ? '+' : '';
    const displayData = useMemo(() => {
        if (!throttledPrice24h || Math.abs(throttledPrice24h) < 0.001) {
            return 0;
        }
        return formatDisplay(throttledPrice24h, {
            decimalToShow: Math.abs(throttledPrice24h) > 0.01 ? 2 : 3,
        });
    }, [throttledPrice24h]);

    const textColor =
        throttledPrice24h != null && throttledPrice24h >= 0
            ? '#38F24A'
            : '#FF695F';

    return (
        <p
            style={{ color: textColor }}
            className="text-xs font-semibold tabular-nums mt-0.5"
        >
            {throttledPrice24h != null ? `${prefix}${displayData}%` : '--'}
        </p>
    );
});

export function Market() {
    const data = usePairs(useShallow((state) => state));
    const prices = usePrice(useShallow((state: PriceState) => state.data));

    return (
        <div>
            {data.data.map((item) => {
                const currentPriceInfo = prices[String(item.pairIndex)];
                const price = currentPriceInfo?.price;
                const rawPrice24h = currentPriceInfo?.price24h;

                return (
                    <Item item={item} price={price} rawPrice24h={rawPrice24h} />
                );
            })}
        </div>
    );
}

function Item(props: {
    item: PairData;
    rawPrice24h: number | string | undefined;
    price: number | string | undefined;
}) {
    const { item, rawPrice24h, price } = props;
    const { isClose } = useCheckClosePair(item.pairIndex);

    return (
        <Link
            to={`/future?pairIndex=${item.pairIndex}`}
            className="group flex items-center justify-between px-4 py-4 border-t border-slate-50 active:bg-slate-50 transition-colors cursor-pointer bg-white"
        >
            <div className="flex items-center gap-3 w-[146px] shrink-0">
                <div className="flex items-center justify-center size-10 rounded-full bg-yellow-100 text-yellow-600 shrink-0">
                    <img src={item.imageUrl} className="w-10 h-10 rounded-lg" />
                </div>
                <div className="flex flex-col truncate">
                    <div className="flex items-center gap-1">
                        <p className="text-base font-bold text-slate-900">
                            {item.symbol}
                        </p>
                    </div>
                    <p className="text-xs text-slate-500 font-medium truncate">
                        {item.name}
                    </p>
                </div>
            </div>
            <div className="flex justify-center flex-1 px-2">
                <div className="w-full">
                    <ChartView
                        symbol={item.symbol}
                        historyCall={item.historyCall ?? ''}
                        rawPrice24h={rawPrice24h}
                        groupName={item.groupName}
                    />
                </div>
            </div>
            {isClose ? (
                <div className="flex flex-col items-end w-[90px] shrink-0">
                    <p className="text-[#FF695F] text-sm">CLOSE</p>
                </div>
            ) : (
                <div className="flex flex-col items-end w-[90px] shrink-0">
                    <p className="text-[17px] font-medium text-slate-900 tabular-nums tracking-tight">
                        {price ? formatDisplay(Number(price), { decimalToShow: item.decimalsToShow }) : '--'}
                    </p>
                    <Price24h rawPrice24h={rawPrice24h} />
                </div>
            )}
        </Link>
    );
}
